<template>
  <div class="page-view">
    <BasePageHeader :title="$t('tenantUsers.heading')" @back="$router.back()">
      <template #actions>
        <router-link to="/bulk-edit/users" class="btn ghost small">{{ $t('bulkEdit.title') }}</router-link>
        <button class="btn primary" @click="$router.push({ name: 'AdminUserDetail', params: { userId: 'new' } })">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <line x1="19" y1="8" x2="19" y2="14"/>
            <line x1="22" y1="11" x2="16" y2="11"/>
          </svg>
          {{ $t('tenantUsers.addUser') }}
        </button>
      </template>
    </BasePageHeader>

    <!-- Stats -->
    <div v-if="!loading && users.length" class="grid grid-cols-3 gap-3 px-5 pt-4 pb-4">
      <div class="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[0.625rem] px-4 py-3.5 text-center">
        <div class="text-[1.75rem] font-bold text-blue-500 leading-none">{{ users.length }}</div>
        <div class="text-xs text-[var(--color-muted)] mt-1.5 uppercase tracking-wide">{{ $t('tenantUsers.total') }}</div>
      </div>
      <div class="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[0.625rem] px-4 py-3.5 text-center">
        <div class="text-[1.75rem] font-bold text-blue-500 leading-none">{{ activeCount }}</div>
        <div class="text-xs text-[var(--color-muted)] mt-1.5 uppercase tracking-wide">{{ $t('common.active') }}</div>
      </div>
      <div class="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[0.625rem] px-4 py-3.5 text-center">
        <div class="text-[1.75rem] font-bold text-blue-500 leading-none">{{ inactiveCount }}</div>
        <div class="text-xs text-[var(--color-muted)] mt-1.5 uppercase tracking-wide">{{ $t('common.inactive') }}</div>
      </div>
    </div>

    <!-- Search & Filters -->
    <div class="px-5 pb-4 flex flex-col gap-3">
      <!-- Search input -->
      <SearchField v-model="query" :debounce="0" :placeholder="$t('tenantUsers.searchPlaceholder')" />

      <!-- Filter pills -->
      <div class="flex flex-wrap gap-2">
        <!-- Status filter -->
        <button
          v-for="s in statusOptions" :key="s.value"
          class="px-3 py-1 rounded-full text-xs font-medium border transition-all cursor-pointer"
          :class="statusFilter === s.value
            ? 'bg-blue-600 border-blue-500 text-white'
            : 'bg-transparent border-[var(--color-border)] text-[var(--color-muted)] hover:border-gray-500 hover:text-[var(--color-text)]'"
          @click="statusFilter = s.value"
        >{{ s.label }}</button>

        <!-- Role filter -->
        <button
          v-for="r in roleOptions" :key="r"
          class="px-3 py-1 rounded-full text-xs font-medium border transition-all cursor-pointer"
          :class="roleFilter === r
            ? 'bg-[var(--color-accent)] border-[var(--color-accent)] text-black'
            : 'bg-transparent border-[var(--color-border)] text-[var(--color-muted)] hover:border-gray-500 hover:text-[var(--color-text)]'"
          @click="roleFilter = roleFilter === r ? null : r"
        >{{ r }}</button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="px-5 flex flex-col gap-3.5">
      <div v-for="i in 3" :key="i" class="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[0.875rem] p-5 flex gap-4 animate-pulse pointer-events-none">
        <div class="w-14 h-14 rounded-full bg-gray-700 flex-shrink-0"></div>
        <div class="flex-1 flex flex-col gap-2.5">
          <div class="h-5 w-2/5 bg-gray-700 rounded"></div>
          <div class="h-4 w-3/5 bg-gray-700 rounded"></div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <template v-else-if="loadError">
      <EmptyState :message="loadError" variant="error" />
      <div class="flex justify-center mt-2">
        <button class="btn ghost sm" @click="load">{{ $t('tenantUsers.tryAgain') }}</button>
      </div>
    </template>

    <!-- Empty State -->
    <div v-else-if="!filtered.length" class="text-center py-16 px-5 flex flex-col items-center gap-3">
      <p class="text-xl font-semibold text-[var(--color-text)] m-0">{{ query ? $t('tenantUsers.noUsersFound') : $t('tenantUsers.noTeamMembers') }}</p>
      <p class="text-sm text-[var(--color-muted)] m-0">{{ query ? $t('tenantUsers.tryAdjust') : $t('tenantUsers.getStartedHint') }}</p>
      <button v-if="!query" class="btn primary small" @click="$router.push({ name: 'AdminUserDetail', params: { userId: 'new' } })">{{ $t('tenantUsers.addFirstUser') }}</button>
    </div>

    <!-- Users List -->
    <div v-else class="px-5 flex flex-col gap-3.5">
      <div
        v-for="u in filtered"
        :key="u.id"
        class="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[0.875rem] p-5 flex gap-4 transition-all cursor-pointer hover:border-gray-500"
        :class="{ 'opacity-60': !u.is_active }"
        @click="$router.push({ name: 'AdminUserDetail', params: { userId: u.id } })"
      >
        <div
          class="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0 text-white"
          :class="u.is_active ? 'bg-gradient-to-br from-blue-500 to-blue-800 shadow-[0_4px_6px_rgba(59,130,246,0.2)]' : 'bg-gradient-to-br from-gray-700 to-gray-800 !text-gray-500'"
        >
          {{ initials(u) }}
        </div>
        <div class="flex-1 min-w-0 flex flex-col gap-2">
          <div>
            <h3 class="text-base font-semibold text-[var(--color-text)] m-0 flex items-center gap-2.5">
              {{ fullName(u) || u.email }}
              <span v-if="!u.is_active" class="badge badge-danger">{{ $t('common.inactive') }}</span>
            </h3>
            <p class="text-sm text-[var(--color-muted)] mt-0.5 mb-0">{{ u.email }}</p>
          </div>

          <div v-if="u.profile?.phone || u.profile?.unique_tag" class="flex flex-wrap gap-4">
            <div v-if="u.profile?.phone" class="flex items-center gap-1.5 text-sm text-[var(--color-muted)]">
              <svg class="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
              {{ u.profile.phone }}
            </div>
            <div v-if="u.profile?.unique_tag" class="flex items-center gap-1.5 text-sm text-[var(--color-muted)]">
              <svg class="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                <line x1="7" y1="7" x2="7.01" y2="7"/>
              </svg>
              {{ u.profile.unique_tag }}
            </div>
          </div>

          <div v-if="u.roles?.length" class="flex flex-wrap gap-2">
            <span v-for="r in u.roles" :key="r.id" class="badge badge-accent">{{ r.name }}</span>
          </div>
        </div>
        <!-- Chevron -->
        <div class="flex items-center flex-shrink-0 text-[var(--color-muted)]">
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { getUsers, getTenantRoles } from '@/api/api.js'
import { useAuthStore } from '@/stores/auth.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import SearchField from '@/components/search/SearchField.vue'

const authStore = useAuthStore()
const { t } = useI18n()
let tenantSlug = ''
watch(() => authStore.currentTenant?.slug, (slug) => { tenantSlug = slug ?? '' }, { immediate: true })

const query        = ref('')
const statusFilter = ref('all')  // 'all' | 'active' | 'inactive'
const roleFilter   = ref(null)   // role name string or null
const users        = ref([])
const allRoles     = ref([])
const loading      = ref(false)
const loadError    = ref(null)

const statusOptions = computed(() => [
  { value: 'all',      label: t('tenantUsers.filterAll') },
  { value: 'active',   label: t('tenantUsers.filterActive') },
  { value: 'inactive', label: t('tenantUsers.filterInactive') },
])

const roleOptions = computed(() => [...allRoles.value].map(r => r.name).sort())

const activeCount   = computed(() => users.value.filter(u => u.is_active).length)
const inactiveCount = computed(() => users.value.filter(u => !u.is_active).length)

const filtered = computed(() => {
  let result = users.value

  if (statusFilter.value === 'active')   result = result.filter(u => u.is_active)
  if (statusFilter.value === 'inactive') result = result.filter(u => !u.is_active)
  if (roleFilter.value) result = result.filter(u => u.roles?.some(r => r.name === roleFilter.value))

  const q = query.value.trim().toLowerCase()
  if (q) result = result.filter(u =>
    fullName(u).toLowerCase().includes(q) ||
    u.email?.toLowerCase().includes(q) ||
    u.profile?.phone?.toLowerCase().includes(q) ||
    u.profile?.unique_tag?.toLowerCase().includes(q)
  )
  return result
})

function fullName(u) {
  return [u.first_name, u.last_name].filter(Boolean).join(' ')
}

function initials(u) {
  const f = u.first_name?.[0] ?? ''
  const l = u.last_name?.[0] ?? ''
  return (f + l).toUpperCase() || (u.email?.[0]?.toUpperCase() ?? '?')
}

async function load() {
  loading.value = true
  loadError.value = null
  try {
    const [res, roles] = await Promise.all([
      getUsers(),
      getTenantRoles(tenantSlug).catch(() => []),
    ])
    users.value    = res.results ?? res ?? []
    allRoles.value = roles ?? []
  } catch {
    loadError.value = t('tenantUsers.loadFailed')
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

